import { z } from 'zod';
import {
  birthdateSchema,
  emailSchema,
  firstNameSchema,
  lastNameSchema,
  phoneSchema
} from './common/schemas';

const formCampusVeranoWindsurfareaSchema = ({
  minAge,
  maxAge
}: {
  minAge?: number;
  maxAge?: number;
}) =>
  z.object({
    user: z.object({
      firstName: firstNameSchema,
      lastName: lastNameSchema,
      phone: phoneSchema,
      identification: z
        .object({
          number: z.string(),
          type: z.string()
        })
        .partial(),
      email: emailSchema,
      address: z.string().min(1),
      location: z.string().min(1),
      postalCode: z.string().min(1)
    }),
    parent: z
      .object({
        firstName: z.string().optional(),
        lastName: z.string().optional(),
        phone: z.string().optional(),
        email: z.string().optional(),
        address: z.string().optional(),
        location: z.string().optional(),
        postalCode: z.string().optional(),
        identification: z
          .object({
            number: z.string().optional(),
            type: z.string().optional()
          })
          .optional()
      })
      .superRefine((data, ctx) => {
        const {
          firstName,
          lastName,
          phone,
          email,
          address,
          location,
          postalCode,
          identification
        } = data;

        const values = [
          firstName,
          lastName,
          phone,
          email,
          address,
          location,
          postalCode,
          identification?.type,
          identification?.number
        ];

        const isEmpty = values.every((val) => !val || val === '');

        if (isEmpty) return; // Nada que validar

        const entries = [
          { key: 'firstName', value: firstName },
          { key: 'lastName', value: lastName },
          { key: 'phone', value: phone },
          { key: 'email', value: email },
          { key: 'address', value: address },
          { key: 'location', value: location },
          { key: 'postalCode', value: postalCode },
          { key: 'identification.type', value: identification?.type },
          { key: 'identification.number', value: identification?.number }
        ];

        for (const entry of entries) {
          if (!entry.value || entry.value.length < 2) {
            if (entry.key === 'email') {
              ctx.addIssue({
                path: entry.key.split('.'),
                message: 'schemas:common.emailRequired',
                code: z.ZodIssueCode.custom
              });
            } else {
              ctx.addIssue({
                path: entry.key.split('.'),
                message: 'schemas:common.fieldRequired',
                code: z.ZodIssueCode.custom
              });
            }
          }
        }
      }),
    student: z.object({
      firstName: firstNameSchema,
      lastName: lastNameSchema,
      identification: z
        .object({
          number: z.string(),
          type: z.string()
        })
        .partial(),
      birthdate: birthdateSchema({
        minAge: minAge,
        maxAge: maxAge
      }),
      illness: z.discriminatedUnion(
        'value',
        [
          z.object({
            value: z.literal<boolean>(true, {
              invalid_type_error: 'schemas:common.fieldObligatory'
            }),
            answer: z
              .string({ required_error: 'schemas:common.fieldObligatory' })
              .min(1, 'schemas:common.fieldRequired')
          }),
          z.object({
            value: z.literal<boolean>(false, {
              invalid_type_error: 'schemas:common.fieldObligatory'
            }),
            answer: z
              .string()
              .optional()
              .transform(() => '')
          })
        ],
        {
          errorMap: (issue, ctx) => ({
            message:
              issue.code === 'invalid_union_discriminator'
                ? `schemas:common.fieldObligatory`
                : ctx.defaultError
          })
        }
      )
    }),
    acceptDataPrivacy: z.boolean().refine((val) => val === true, {
      message: 'schemas:common.dataPrivacyRequired'
    }),
    acceptTakePhotos: z.boolean().optional(),
    authOfferProducts: z.boolean().optional()
  });

export default formCampusVeranoWindsurfareaSchema;